import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Campaign } from '../../../../../../types/campaign.interface';
import { formatCampaignDateTime } from '../../../../../../types/campaign.utils';
import { formatDate } from '../../../../../../utils/date.utils';
import {
  ChosenSource,
  CONCAT_ENTITY_TYPES,
  ConflictResolution,
  MergeAnalysis,
  MergeConflict,
  SCALAR_GENERAL_ENTITY_TYPES,
} from '../../../../../../types/campaign-merge.interface';
import { ConfirmationHelperService } from '../../../../../../services/confirmation-helper.service';
import { CesButtonComponent, CesButtonTertiaryComponent } from '../../../../../ui/ces-buttons';
import { CesDeleteButtonComponent } from '../../../../../ui/ces-buttons/ces-delete-button.component';

interface ConflictDialogBaseData {
  // Only the conflict rows are rendered; the merge ids stay with the merge flow.
  analysis: Pick<MergeAnalysis, 'conflicts'>;
}

// Discriminated on `mode`: campaign flows must hand over both campaigns, a manual import has none.
export type ConflictDialogData =
  | (ConflictDialogBaseData & { mode: 'manual-import' })
  | (ConflictDialogBaseData & { mode: 'merge' | 'import-pda' | 'import-web'; campaignA: Campaign; campaignB: Campaign });

export interface ConflictDialogResult {
  resolutions?: ConflictResolution[];
  abandoned?: boolean;
}

interface ConflictRow {
  uid: string;
  globalIndex: number;
  conflict: MergeConflict;
  chosenSource: ChosenSource | null;
}

@Component({
  selector: 'app-campaign-conflict-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoModule, CesButtonComponent, CesDeleteButtonComponent, CesButtonTertiaryComponent],
  templateUrl: './campaign-conflict-dialog.component.html',
})
export class CampaignConflictDialogComponent {
  private readonly ref = inject(DynamicDialogRef);
  private readonly config = inject<DynamicDialogConfig<ConflictDialogData>>(DynamicDialogConfig);
  private readonly confirmationHelper = inject(ConfirmationHelperService);
  private readonly translate = inject(TranslocoService);

  private readonly data: ConflictDialogData | undefined = this.config.data;

  readonly conflictRows = signal<ConflictRow[]>(
    (this.data?.analysis.conflicts ?? []).map((conflict: MergeConflict, i: number) => ({
      uid: `${conflict.entityType}_${conflict.entityKey}`,
      globalIndex: i,
      conflict,
      chosenSource: null as 'a' | 'b' | 'concat' | null,
    })),
  );

  readonly measureRows = computed(() => this.conflictRows().filter(r => !CONCAT_ENTITY_TYPES.has(r.conflict.entityType)));

  readonly generalRows = computed(() => this.conflictRows().filter(r => CONCAT_ENTITY_TYPES.has(r.conflict.entityType)));

  readonly allResolved = computed(() => this.conflictRows().every(r => r.chosenSource !== null));
  readonly resolvedCount = computed(() => this.conflictRows().filter(r => r.chosenSource !== null).length);

  readonly dateA = computed(() => {
    const data = this.data;
    return data !== undefined && data.mode !== 'manual-import' ? formatCampaignDateTime(data.campaignA) : 'A';
  });

  readonly dateB = computed(() => {
    const data = this.data;
    return data !== undefined && data.mode !== 'manual-import' ? formatCampaignDateTime(data.campaignB) : 'B';
  });

  readonly mode = computed(() => this.data?.mode ?? 'merge');
  readonly isImport = computed(() => this.mode() !== 'merge');
  // A PDA import can be deferred: cancel keeps the pending conflicts for later. A file import cannot
  // (the file can simply be re-imported), so abandoning replaces cancel as its only non-validate exit.
  readonly showAbandonImport = computed(() => this.mode() === 'import-pda' || this.mode() === 'import-web');
  readonly showCancel = computed(() => this.mode() !== 'import-web');

  readonly headerA = computed(() => {
    switch (this.mode()) {
      case 'manual-import':
        return this.translate.translate('entities.manualImport.execute.headerExisting');
      case 'merge':
        return this.translate.translate('entities.campaign.merge.directionSource');
      default:
        return this.translate.translate('entities.campaign.import.headerCesmon');
    }
  });

  readonly headerB = computed(() => {
    switch (this.mode()) {
      case 'import-pda':
        return this.translate.translate('entities.campaign.import.headerPda');
      case 'import-web':
        return this.translate.translate('entities.campaign.import.headerImport');
      case 'manual-import':
        return this.translate.translate('entities.manualImport.execute.headerImport');
      default:
        return this.translate.translate('entities.campaign.merge.directionTarget');
    }
  });

  // The warning names what collides: two campaigns for merges/imports, draft vs stored data for manual imports.
  readonly conflictsWarning = computed(() =>
    this.mode() === 'manual-import'
      ? this.translate.translate('entities.manualImport.execute.conflictsWarning')
      : this.translate.translate('entities.campaign.merge.conflictsWarning'),
  );

  readonly selectAllALabel = computed(() => {
    switch (this.mode()) {
      case 'manual-import':
        return this.translate.translate('entities.manualImport.execute.selectAllExisting');
      case 'merge':
        return this.translate.translate('entities.campaign.merge.selectAllSource');
      default:
        return this.translate.translate('entities.campaign.merge.selectAllA');
    }
  });

  readonly selectAllBLabel = computed(() => {
    switch (this.mode()) {
      case 'manual-import':
        return this.translate.translate('entities.manualImport.execute.selectAllImported');
      case 'merge':
        return this.translate.translate('entities.campaign.merge.selectAllTarget');
      default:
        return this.translate.translate('entities.campaign.merge.selectAllB');
    }
  });

  setResolution(globalIndex: number, source: ChosenSource): void {
    this.conflictRows.update(rows => rows.map(row => (row.globalIndex === globalIndex ? { ...row, chosenSource: source } : row)));
  }

  selectAll(source: 'a' | 'b'): void {
    this.conflictRows.update(rows => rows.map(row => ({ ...row, chosenSource: source })));
  }

  cancel(): void {
    this.ref.close();
  }

  // Caution variant (triangle), not a destructive delete: abandoning clears the import draft but
  // destroys no stored campaign, so it must render amber rather than the red trash card.
  async abandonImport(): Promise<void> {
    const confirmed = await this.confirmationHelper.ask(
      'entities.campaign.import.abandonConfirmMessage',
      'entities.campaign.import.abandonConfirmHeader',
      undefined,
      'pi pi-exclamation-triangle',
      'entities.campaign.actions.abandonImport',
    );
    if (confirmed) this.ref.close({ abandoned: true });
  }

  validate(): void {
    if (!this.allResolved()) return;
    const resolutions: ConflictResolution[] = this.conflictRows().flatMap(row =>
      row.chosenSource !== null
        ? [{ entityType: row.conflict.entityType, entityKey: row.conflict.entityKey, chosenSource: row.chosenSource }]
        : [],
    );
    this.ref.close({ resolutions });
  }

  /** Scalar general fields (e.g. timestamp) cannot be concatenated; manual-import resolutions are binary keep/overwrite. */
  supportsConcat(row: ConflictRow): boolean {
    return this.mode() !== 'manual-import' && !SCALAR_GENERAL_ENTITY_TYPES.has(row.conflict.entityType);
  }

  /** Render a conflict value, formatting ISO timestamps with the locale-aware date helper. */
  formatValue(row: ConflictRow, raw: string | number | null | undefined): string {
    if (raw === null || raw === undefined || raw === '') return '-';
    if (row.conflict.entityType === 'timestamp' && typeof raw === 'string') {
      return formatDate(raw);
    }
    return String(raw);
  }
}
